import { ComponentProps, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormInputState } from '@/hooks/useFormInputState';
import { hasOnlyLatinChars, hasOnlyNumbers } from '@/utils/formUtils';

import FormErrorV2 from '@/components/common/form/FormErrorV2';
import Icon from '@/components/common/iconedLinks/Icon';

import styles from './FormFieldV2.module.scss';
import { ERROR_TYPE, POSTAL_CODE_REGEX } from '@/constants/form';
import { URLS } from '@/constants/urls';

export type PostalCodeFieldV2Props = {
  id?: string;
  label: string;
  validators?: string[];
  country?: string;
  hintMessage?: string;
  rest?: Record<string, any>;
 } & ComponentProps<'input'>;

export const PostalCodeFieldV2 = (props: PostalCodeFieldV2Props): React.JSX.Element => {
  const { id, name, label, validators, required, disabled, country, hintMessage, ...rest } = props;

  const { register, formState: { errors }, clearErrors } = useFormContext();
  const { isFilled } = useFormInputState({ name });

  const isPostalCodeValidated = async (newPc) => {
    if (newPc && !newPc.match(POSTAL_CODE_REGEX[country])) {
      return false;
    }
    clearErrors(name);
    return true;
  };

  const validate = Object.fromEntries([
    [ERROR_TYPE.POSTAL_CODE, isPostalCodeValidated],
    validators?.includes(ERROR_TYPE.ONLY_NUMBERS) && [ERROR_TYPE.ONLY_NUMBERS, hasOnlyNumbers],
    validators?.includes(ERROR_TYPE.ONLY_LATIN_CHARS) && [ERROR_TYPE.ONLY_LATIN_CHARS, hasOnlyLatinChars]
  ].filter(Boolean));

  const [isFocused, setIsFocused] = useState(false);

  const labelClassName = [
    styles.label,
    (isFilled || isFocused) ? styles['label-visible'] : styles['label-invisible']
  ].filter(Boolean).join(' ');

  const inputProps = {
    id,
    rbf: `input${id ? '-' + id : ''}`,
    className: [
      styles.input,
      (isFilled || isFocused) && styles['input-focus'],
      errors?.[name] && styles['input-error']
    ].filter(Boolean).join(' '),
    name,
    placeholder: label,
    disabled,
    ...register(name, {
      required,
      validate
    }),
    ...rest
  };

  return (
    <div className={styles.container}>
      <label className={labelClassName}>{label}</label>
      {errors?.[name] && (
        <Icon
          src={URLS.DELETE_CIRCLE_ICON_RED}
          alt={'arrow'}
          width={20}
          height={20}
          className={styles['icon']}
        />
      )}
      <input
        {...inputProps}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
      />
      {hintMessage && <div className={styles.hint}>{hintMessage}</div>}
      {errors?.[name] && <FormErrorV2 error={errors[name]} />}
    </div>
  );
};

export default PostalCodeFieldV2;
